import { useEffect, useMemo, useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { api } from '../../lib/api';
import type { Inbox } from '../../lib/types';
import { InboxPicker } from './InboxPicker';
import { sx } from './styles';

interface AutonomyConfig {
  enabled?: boolean;
  minIntentConfidence?: number;
  minTopScore?: number;
  suggestIntents?: string[];
}
interface InboxAiConfig {
  aiEnabled?: boolean;
  aiMode?: 'auto' | 'suggest' | 'off';
  autonomy?: AutonomyConfig;
  [k: string]: unknown;
}
interface InboxWithConfig extends Inbox {
  config?: InboxAiConfig;
}

type Handling = 'autopilot' | 'review' | 'off';

function handlingOf(cfg?: InboxAiConfig): Handling {
  if (cfg?.aiEnabled === false || cfg?.aiMode === 'off') return 'off';
  if (cfg?.aiMode === 'suggest') return 'review';
  return 'autopilot';
}

const OPTIONS: { value: Handling; title: string; desc: string }[] = [
  { value: 'autopilot', title: 'Autopilot', desc: 'AI answers customers automatically.' },
  { value: 'review', title: 'Review first (human-in-the-loop)', desc: 'AI drafts a reply; an agent approves or edits before it is sent.' },
  { value: 'off', title: 'Off', desc: 'No AI on this inbox — every message goes straight to an agent.' },
];

export function AiModePanel({ canEdit }: { canEdit: boolean }) {
  const { items: inboxes, loading } = useResource<InboxWithConfig>('inboxes');
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [handling, setHandling] = useState<Handling>('autopilot');
  const [holdEnabled, setHoldEnabled] = useState(false);
  const [minConf, setMinConf] = useState(0.6);
  const [minKb, setMinKb] = useState(0.45);
  const [intents, setIntents] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const selected = useMemo(() => inboxes.find((i) => i.id === inboxId) || null, [inboxes, inboxId]);

  useEffect(() => {
    if (!inboxId && inboxes.length > 0) setInboxId(inboxes[0].id);
  }, [inboxes, inboxId]);

  useEffect(() => {
    if (!selected) return;
    const cfg = selected.config ?? {};
    setHandling(handlingOf(cfg));
    const a = cfg.autonomy ?? {};
    setHoldEnabled(a.enabled === true);
    setMinConf(typeof a.minIntentConfidence === 'number' ? a.minIntentConfidence : 0.6);
    setMinKb(typeof a.minTopScore === 'number' ? a.minTopScore : 0.45);
    setIntents(Array.isArray(a.suggestIntents) ? a.suggestIntents.join(', ') : '');
    setSaved(false);
  }, [selected]);

  async function save() {
    if (!inboxId || !selected) return;
    setSaving(true);
    setSaved(false);
    const aiEnabled = handling !== 'off';
    const aiMode: 'auto' | 'suggest' | 'off' = handling === 'review' ? 'suggest' : handling === 'off' ? 'off' : 'auto';
    const autonomy: AutonomyConfig = {
      enabled: handling === 'autopilot' && holdEnabled,
      minIntentConfidence: minConf,
      minTopScore: minKb,
      suggestIntents: intents.split(',').map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.patch('inboxes', inboxId, {
        config: { ...(selected.config ?? {}), aiEnabled, aiMode, autonomy },
      });
      setSaved(true);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 4 }}>AI mode</h2>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
        Decide whether the AI answers on its own, drafts replies for an agent to approve
        (human-in-the-loop), or is off — per inbox.
      </p>
      {loading && <div>Loading…</div>}
      <InboxPicker inboxes={inboxes} value={inboxId} onChange={setInboxId} />

      {selected && (
        <>
          <div style={sx.card}>
            {OPTIONS.map((o) => (
              <label
                key={o.value}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 12px',
                  border: `1px solid ${handling === o.value ? '#2563eb' : '#e5e7eb'}`,
                  background: handling === o.value ? '#eff6ff' : 'white',
                  borderRadius: 8,
                  marginBottom: 8,
                  cursor: canEdit ? 'pointer' : 'default',
                }}
              >
                <input
                  type="radio"
                  name="aiHandling"
                  checked={handling === o.value}
                  onChange={() => setHandling(o.value)}
                  disabled={!canEdit}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{o.title}</span>
                  <br />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{o.desc}</span>
                </span>
              </label>
            ))}
          </div>

          {handling === 'autopilot' && (
            <div style={sx.card}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={holdEnabled}
                  onChange={(e) => setHoldEnabled(e.target.checked)}
                  disabled={!canEdit}
                />
                Hold uncertain replies for review (graduated human-in-the-loop)
              </label>
              <p style={{ margin: '6px 0 10px 24px', fontSize: 12, color: '#6b7280' }}>
                On Autopilot, when the bot is unsure it holds its draft for an agent instead of
                auto-sending. Confident replies still go out automatically.
              </p>
              {holdEnabled && (
                <div style={{ marginLeft: 24, display: 'grid', gap: 10, maxWidth: 420 }}>
                  <label style={sx.label}>
                    Auto-send only if confidence ≥ {minConf.toFixed(2)}
                    <input type="range" min={0} max={1} step={0.05} value={minConf}
                      onChange={(e) => setMinConf(Number(e.target.value))} disabled={!canEdit}
                      style={{ width: '100%' }} />
                  </label>
                  <label style={sx.label}>
                    Auto-send only if knowledge-base match ≥ {minKb.toFixed(2)}
                    <input type="range" min={0} max={1} step={0.05} value={minKb}
                      onChange={(e) => setMinKb(Number(e.target.value))} disabled={!canEdit}
                      style={{ width: '100%' }} />
                  </label>
                  <label style={sx.label}>
                    Always review these topics (optional, comma-separated)
                    <input type="text" value={intents} placeholder="billing, porting"
                      onChange={(e) => setIntents(e.target.value)} disabled={!canEdit}
                      style={{ ...sx.input, maxWidth: '100%' }} />
                  </label>
                </div>
              )}
            </div>
          )}

          {canEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={save} disabled={saving} style={sx.btnPrimary}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && <span style={{ fontSize: 13, color: '#059669' }}>✓ Saved — takes effect on the next message</span>}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af' }}>Only an admin can change AI mode.</p>
          )}
        </>
      )}
    </section>
  );
}
